<script lang="ts">
	// DS
	import {
		DropdownMenu,
		MenuItemList,
		type IconButtonPropsTypes,
	} from '@Shelly/shelly-design-system/index';
	// Components
	import OptionManagmentModal from 'src/components/OptionManagmentModal.svelte';
	import TabsActionsButton from 'src/components/TabsActionsButton.svelte';
	// Stores
	import { is_mobile, is_tablet } from 'src/stores/ui/viewport';
	// Types
	import type { MenuItemListExtendedProps } from '../dash-types';
	import { mapIconSolidWeightToLight } from 'src/helpers/new-layout-helpers';

	export let menuListItemsData: MenuItemListExtendedProps[];
	/**
	 * Trigger button style. The dashboard tabs action slot passes `help-2` for
	 * a transparent kebab that matches the dashboard header treatment.
	 */
	export let triggerVariant: IconButtonPropsTypes['variant'] = 'secondary';

	let showDropdown = false;
	let showModal = false;

	const toggleDashActions = () => {
		if ($is_tablet || $is_mobile) {
			showModal = !showModal;
		} else if (!$is_tablet && !$is_mobile) {
			showDropdown = !showDropdown;
		}
	};

	function prepareModalData() {
		return {
			topSectionTitle: menuListItemsData[0]?.title || 'Manage Dashboard',
			itemsTop:
				menuListItemsData[0]?.items?.map((item) => ({
					...item,
					title: item.title,
					titleDefault: item.title,
					icon: mapIconSolidWeightToLight(item.iconMobile || item.icon),
					disabled: item.disabled || false,
					onClick: item.onClick,
				})) || [],

			bottomSectionTitle: menuListItemsData[1]?.title || 'Manage Widgets',
			// Collapse every group after the first into the bottom section so menus with
			// more than two groups (e.g. the dashboard tabs kebab) keep parity on tablet.
			itemsBottom:
				menuListItemsData
					.slice(1)
					.flatMap((group) => group?.items || [])
					.map((item) => ({
						...item,
						title: item.title,
						titleDefault: item.title,
						icon: mapIconSolidWeightToLight(item.iconMobile || item.icon),
						disabled: item.disabled || false,
						onClick: item.onClick,
					})) || [],
		};
	}

	let modalConfig = prepareModalData();

	$: (menuListItemsData, (modalConfig = prepareModalData()));
</script>

<DropdownMenu bind:show={showDropdown}>
	<TabsActionsButton
		slot="trigger"
		variant={triggerVariant}
		isActive={showDropdown}
		on:click={toggleDashActions}
	/>

	{#each menuListItemsData as groupItem, groupIndex}
		{#if groupItem.title}
			<MenuItemList isGroupTitle={groupItem.isGroupTitle} size="m" title={groupItem.title} />
		{:else if groupIndex > 0}
			<!-- Title-less group (e.g. "Add dashboard") renders as a standalone divider. -->
			<div class="dash-actions-divider" />
		{/if}

		{#each groupItem.items as menuListItem}
			<MenuItemList
				isGroupItem
				size="m"
				title={menuListItem.title}
				titleClasses={menuListItem.titleClasses}
				icon={menuListItem.icon}
				iconStyle={menuListItem?.iconStyle}
				disabled={menuListItem.disabled}
				on:click={() => {
					toggleDashActions();
					menuListItem.onClick(menuListItem);
				}}
			/>
		{/each}
	{/each}
</DropdownMenu>

<OptionManagmentModal
	bind:showModal
	topSectionTitle={modalConfig.topSectionTitle}
	bottomSectionTitle={modalConfig.bottomSectionTitle}
	itemsTop={modalConfig.itemsTop}
	itemsBottom={modalConfig.itemsBottom}
/>

<style>
	.dash-actions-divider {
		border-top: 1px solid var(--color-3);
	}
</style>
